/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { TokenMetadata, getLocalTokens } from '../pinata';
import { AiSignal, TokenAnalytics, BotPerformance, SmartMoneyTrader } from '../../types/ai';
import { MARKET_DATA } from '../../data/market-data';
import { connection } from '../solana/solana';
import { formatAddress } from '../utils/format';

export interface SwarmStatus {
    activeNodes: number;
    consensusRate: number;
    networkTps: number;
    lastBlock: number;
}

// --- SWARM INTELLIGENCE ENGINE ---

export class AiEngine {
    
    static async getSwarmStatus(): Promise<SwarmStatus> {
        // Fetch real blockchain stats to inform the swarm status
        let tps = 2400;
        let slot = 0;
        
        try {
            const samples = await connection.getRecentPerformanceSamples(1);
            if (samples.length > 0) {
                const sample = samples[0];
                tps = Math.floor(sample.numTransactions / sample.samplePeriodSecs);
                slot = sample.slot;
            } else {
                slot = await connection.getSlot();
            }
        } catch (e) {
            console.warn("Failed to fetch chain stats for swarm, using estimates.");
        }

        return {
            activeNodes: 124 + Math.floor(Math.random() * 20), // Simulated active analysis nodes
            consensusRate: 85 + (Math.random() * 14), // % agreement between nodes
            networkTps: tps,
            lastBlock: slot
        };
    }

    /**
     * Simulates fetching top performing wallets ("Smart Money")
     */
    static async getSmartMoneyTraders(): Promise<SmartMoneyTrader[]> {
        // Simulate DB fetch latency
        await new Promise(r => setTimeout(r, 400));

        const traders: SmartMoneyTrader[] = Array.from({ length: 8 }).map((_, i) => {
            const isWin = Math.random() > 0.3;
            const pnl = Math.floor(Math.random() * 150000) + 10000;
            const token = MARKET_DATA[Math.floor(Math.random() * MARKET_DATA.length)];
            
            return {
                address: i === 0 ? '7xKX...9jL2' : formatAddress(`0x${Math.random().toString(16).slice(2).repeat(2)}`, 6),
                pnl: isWin ? pnl : -pnl * 0.1,
                winRate: 60 + Math.floor(Math.random() * 35),
                totalVolume: pnl * (Math.floor(Math.random() * 10) + 1),
                badges: Math.random() > 0.7 ? ['Whale'] : Math.random() > 0.5 ? ['Sniper'] : [],
                lastTrade: {
                    type: Math.random() > 0.5 ? 'BUY' : 'SELL',
                    symbol: token.symbol,
                    tokenMint: token.properties?.mintAddress || '',
                    time: Date.now() - Math.floor(Math.random() * 3600000),
                    amount: Math.floor(Math.random() * 5000),
                    pnl: Math.random() > 0.5 ? Math.floor(Math.random() * 1000) : undefined
                }
            };
        });

        return traders.sort((a, b) => b.pnl - a.pnl);
    }

    /**
     * Simulates a heavy ML inference call to analyze a specific token.
     * Enhanced with Risk & Volume Analysis.
     */
    static async analyzeToken(token: TokenMetadata, currentPrice: number): Promise<TokenAnalytics> {
        // Simulate network latency for inference
        await new Promise(r => setTimeout(r, 800));

        const seed = (token.name.length + token.symbol.length + currentPrice);
        const isBullish = Math.random() > 0.4;
        const volatility = Math.floor(Math.random() * 80) + 20;
        const chain = token.properties?.chain || 'SOL';
        
        // Generate a random walk for prediction graph
        const graph = [];
        let price = currentPrice;
        for(let i=0; i<24; i++) {
            const change = (Math.random() - 0.5) * (volatility / 500);
            price = price * (1 + change);
            graph.push({ time: `${i}:00`, price });
        }

        const riskScore = Math.random() * 100;
        let riskLevel: any = 'LOW';
        if (riskScore > 50) riskLevel = 'MEDIUM';
        if (riskScore > 80) riskLevel = 'HIGH';
        if (token.symbol === 'VOID') riskLevel = 'CRITICAL'; 

        const auditFlags = riskLevel === 'HIGH' ? ['Mint Auth Active', 'Low Liquidity'] : ['Audited'];
        if (chain === 'SUI') auditFlags.push('Move Verified');
        else if (chain === 'SOL') auditFlags.push('SPL Standard');

        // Volume Analysis Simulation
        const buyPressure = isBullish ? 55 + Math.random() * 30 : 45 - Math.random() * 30;
        const volumeMomentum = Math.random() > 0.7 ? 'SURGE' : Math.random() > 0.4 ? 'STEADY' : 'DECLINE';

        // Risk Simulation
        const honeypotProb = riskLevel === 'CRITICAL' ? 85 + Math.random() * 10 : Math.random() * 10;
        const rugPullProb = riskLevel === 'HIGH' ? 40 + Math.random() * 20 : Math.random() * 5;

        return {
            score: Math.floor(Math.random() * 40) + 60, // Bias towards decent tokens
            riskLevel,
            sentiment: isBullish ? 'BULLISH' : 'BEARISH',
            predictedTrend: isBullish ? 'UP' : 'DOWN',
            predictedPrice24h: graph[graph.length-1].price,
            volatilityScore: volatility,
            whaleActivity: Math.random() > 0.7 ? 'ACCUMULATING' : 'QUIET',
            summary: this.generateSummary(token.symbol, chain, isBullish, riskLevel, volumeMomentum),
            predictionGraph: graph,
            auditFlags,
            // Enhanced Metrics
            honeypotProbability: Math.floor(honeypotProb),
            rugPullProbability: Math.floor(rugPullProb),
            liquidityAnalysis: {
                lockedAmount: Math.floor(Math.random() * 100000),
                lockDuration: Math.floor(Math.random() * 365),
                isBurned: Math.random() > 0.5
            },
            volumeAnalysis: {
                momentum: volumeMomentum as any,
                buyPressure: Math.floor(buyPressure),
                spikeDetected: volumeMomentum === 'SURGE'
            }
        };
    }

    /**
     * Scans the market for "Buy" opportunities.
     * Uses Swarm Consensus logic (simulated) to filter high-quality signals.
     */
    static async scanForSignals(): Promise<AiSignal[]> {
        const signals: AiSignal[] = [];
        
        // Get local tokens (newly minted by user)
        const localTokens = getLocalTokens().map(t => ({
            ...t,
            price: 1.0, // Default start price for local mocks if not available
            change24h: 0,
            volume24h: 0,
            marketCap: 0,
            launchDate: Date.now()
        }));

        // Combine with static market data
        const allCandidates = [...localTokens, ...MARKET_DATA];
        
        // Swarm Selection: Pick random tokens, ensuring at least one local token if available
        let candidates: any[] = [];
        if (localTokens.length > 0) {
            candidates.push(localTokens[0]);
            const remaining = allCandidates.filter(t => t.symbol !== localTokens[0].symbol);
            candidates = [...candidates, ...remaining.sort(() => 0.5 - Math.random()).slice(0, 5)];
        } else {
            candidates = allCandidates.sort(() => 0.5 - Math.random()).slice(0, 6);
        }

        candidates.forEach(token => {
            const isBuy = Math.random() > 0.5;
            const price = (token as any).price || 1.0;
            const chain = token.properties?.chain || 'SOL';
            
            // Calculate Consensus Score (Mock)
            // In real system, this aggregates votes from multiple model instances
            const nodeVotes = Math.floor(Math.random() * 50) + 50; // 50-100 votes
            const agreement = Math.floor(Math.random() * 20) + 80; // 80-100% agreement
            const confidence = Math.floor((agreement + (nodeVotes / 100 * 20)) / 1.2); // Derived confidence

            // Custom reasoning for user tokens vs static
            const isLocal = !MARKET_DATA.find(m => m.symbol === token.symbol);
            const reasoning = isLocal 
                ? `Swarm Alert: New launch ${token.name} on ${chain}. ${nodeVotes} nodes analyzing early liquidity depth.` 
                : (isBuy 
                    ? `Swarm Consensus (${agreement}%): Volume spike detected on ${chain}. Whale accumulation confirmed by ${nodeVotes} nodes.`
                    : `Swarm Consensus (${agreement}%): RSI Overbought on ${chain} pair. Divergence detected.`);

            signals.push({
                id: `sig_${Math.random().toString(36).slice(2)}`,
                token,
                type: isBuy ? 'BUY' : 'SELL',
                priceAtSignal: price,
                confidence: Math.min(confidence, 99),
                reasoning,
                timestamp: Date.now(),
                status: 'ACTIVE'
            });
        });

        return signals;
    }

    static getBotPerformance(): BotPerformance {
        return {
            totalProfit: 12450.32,
            activePositions: 3,
            winRate: 68.5,
            dailyPnl: 450.20
        };
    }

    private static generateSummary(ticker: string, chain: string, bullish: boolean, risk: string, momentum: string): string {
        const networkTerm = chain === 'SUI' ? "Move object velocity" : "SPL transfer volume";
        
        if (bullish) {
            return `Swarm analysis suggests ${ticker} is bullish with ${momentum.toLowerCase()} momentum. ${networkTerm} is increasing. Risk level: ${risk}.`;
        }
        return `${ticker} showing bearish divergence. ${networkTerm} suggests distribution with ${momentum.toLowerCase()} volume. Risk level: ${risk}.`;
    }
}